import { Injectable, EventEmitter } from '@angular/core';
import { isString, isNumber, isFunction } from './toast.utils';

import { Observable } from 'rxjs/Observable';

/**
 * Options to configure specific Toast
 */
@Injectable()
export class ToastOptions {
  title: string;
  msg?: string;
  showClose?: boolean;
  theme?: string;
  timeout?: number;
  onAdd?: Function;
  onRemove?: Function;
}

/**
 * Structrure of Toast
 */
@Injectable()
export class ToastData {
  id: number;
  title: string;
  msg: string;
  showClose: boolean;
  type: string;
  theme: string;
  timeout: number;
  onAdd: Function;
  onRemove: Function;
  onClick: Function;
}

/**
 * Default configuration for all toasts and toast container
 */
@Injectable()
export class ToastConfig {

  // Maximum number of toasties to show at once
  limit: number = 5;

  // Whether to show the 'X' icon to close the toast
  showClose: boolean = true;

  // The window position where the toast pops up. Possible values
  // bottom-right, bottom-left, top-right, top-left, top-center, bottom-center, center-center
  position: string = 'bottom-right';

  // How long (in miliseconds) the toast shows before it's removed. Set to null/0 to turn off.
  timeout: number = 5000;

  // What theme to use. Possible values:
  // default, material or bootstrap
  theme: string = 'default';
}

export function ToastServiceFactory(config: ToastConfig): ToastService  {
    return new ToastService(config);
}

/**
 * Toast service helps create different kinds of Toasts
 */
@Injectable()
export class ToastService {
  // Allowed THEMES
  static THEMES: Array<string> = ['default'];
  // Init the counter
  uniqueCounter: number = 0;
  // ToastData event emitter
  private toastsEmitter: EventEmitter<ToastData> = new EventEmitter<ToastData>();
  // Clear event emitter
  private clearEmitter: EventEmitter<number> = new EventEmitter<number>();

  constructor(private config: ToastConfig) {}

  /**
   * Get list of toats
   */
  getToasts(): Observable<ToastData> {
    return this.toastsEmitter.asObservable();
  }

  getClear(): Observable<number> {
    return this.clearEmitter.asObservable();
  }

  /**
   * Create Toast of a default type
   */
  default(options: ToastOptions|string|number): void {
    this.add(options, 'default');
  }

  /**
   * Create Toast of info type
   * @param  {object} options Individual toast config overrides
   */
  info(options: ToastOptions|string|number): void {
    this.add(options, 'info');
  }

  /**
   * Create Toast of success type
   * @param  {object} options Individual toast config overrides
   */
  success(options: ToastOptions|string|number): void {
    this.add(options, 'success');
  }

  /**
   * Create Toast of wait type
   * @param  {object} options Individual toast config overrides
   */
  wait(options: ToastOptions|string|number): void {
    this.add(options, 'wait');
  }

  /**
   * Create Toast of error type
   * @param  {object} options Individual toast config overrides
   */
  error(options: ToastOptions|string|number): void {
    this.add(options, 'error');
  }

  /**
   * Create Toast of warning type
   * @param  {object} options Individual toast config overrides
   */
  warning(options: ToastOptions|string|number): void {
    this.add(options, 'warning');
  }

  // Clear all toasts
  clearAll() {
    this.clearEmitter.next(null);
  }

  // Clear the specific one
  clear(id: number) {
    this.clearEmitter.next(id);
  }

  // Add a new toast item
  private add(options: ToastOptions|string|number, type: string) {
    let toastOptions: ToastOptions;

    if (isString(options) && options !== '' || isNumber(options)) {
      toastOptions = <ToastOptions>{
        title: options.toString()
      };
    } else {
      toastOptions = <ToastOptions>options;
    }

    if (!toastOptions || !toastOptions.title && !toastOptions.msg) {
      throw new Error('toast-container: No toast title or message specified!');
    }

    type = type || 'default';

    // Set a unique counter for an id
    this.uniqueCounter++;

    // Set the local vs global config items
    let showClose = this._checkConfigItem(this.config, toastOptions, 'showClose');

    // If we have a theme set, make sure it's a valid one
    let theme: string;
    if (toastOptions.theme) {
      theme = ToastService.THEMES.indexOf(toastOptions.theme) > -1 ? toastOptions.theme : this.config.theme;
    } else {
      theme = this.config.theme;
    }

    let toast: ToastData = <ToastData>{
      id       : this.uniqueCounter,
      title    : toastOptions.title,
      msg      : toastOptions.msg,
      showClose: showClose,
      type     : 'toast-type-' + type,
      theme    : 'toast-theme-' + theme,
      onAdd    : toastOptions.onAdd && isFunction(toastOptions.onAdd) ? toastOptions.onAdd : null,
      onRemove : toastOptions.onRemove && isFunction(toastOptions.onRemove) ? toastOptions.onRemove : null
    };

    // If there's a timeout individually or globally, set the toast to timeout
    // Allows a caller to pass null/0 and override the default. Can also set the default to null/0 to turn off.
    toast.timeout = toastOptions.hasOwnProperty('timeout') ? toastOptions.timeout : this.config.timeout;

    // Push up a new toast item
    // this.toastsSubscriber.next(toast);
    this.toastsEmitter.next(toast);
    // If we have a onAdd function, call it here
    if (toastOptions.onAdd && isFunction(toastOptions.onAdd)) {
      toastOptions.onAdd.call(this, toast);
    }
  }

  // Checks whether the local option is set, if not,
  // checks the global config
  private _checkConfigItem(config: any, options: any, property: string) {
    if (options[property] === false) {
      return false;
    } else if (!options[property]) {
      return config[property];
    } else {
      return true;
    }
  }
}



// WEBPACK FOOTER //
// ./src/common/toasts/toast.service.ts